import React, { useEffect } from 'react';
import ComboGuestMaterialChooser from 'components/show_drawing/redraw_form/ComboGuestMaterialChooser';
import renderOption from 'components/show_drawing/redraw_form/options/renderOption.jsx';
import Constants from 'components/show_drawing/Constants.jsx';
import { readStoredFormulaOptions } from 'components/show_drawing/redraw_form/combo_local_storage.js';
import { ADMIN_DISABLED_WARNING } from 'components/show_drawing/redraw_form/combo_constants.js';
import styles from 'components/show_drawing/redraw_form/_style.module.scss';

// Guest ask options do not participate in the host's user-changed tracking
// or recommendation engine (no recommendations flow to the guest yet). When
// that changes, wire up a guest-scoped tracker instead of these no-ops.
const NO_OP = () => {};
const EMPTY_LIST = [];

export const relationHasRenderableOptions = (relation) => {
  const hasAsks = relation.ask_options && relation.ask_options.length > 0;
  const hasMaterials = relation.materials && (
    (relation.materials.groups && relation.materials.groups.length > 0) ||
    relation.materials.ask_custom_material
  );
  return hasAsks || hasMaterials;
};

// Matches the host's sort: not-advanced first, advanced second; within each
// partition sort by sort_order desc (same rule as
// BuildComboConfirmSectionsService.sort_like_host). Advanced options are
// only included when the host-level "Show advanced options" toggle is on.
const visibleAskOptions = (askOptions, advancedOptionsVisible) => {
  const bySortOrderDesc = (a, b) => (b.sort_order || 0) - (a.sort_order || 0);
  const notAdvanced = askOptions.filter((option) => !option.advanced).sort(bySortOrderDesc);
  if (!advancedOptionsVisible) { return notAdvanced; }
  const advanced = askOptions.filter((option) => option.advanced).sort(bySortOrderDesc);
  return [...notAdvanced, ...advanced];
};

const ComboGuestOptionsSection = ({ combos, activeCombosByGuestName, setActiveCombosByGuestName, setRedrawRequested, unit, register, errors, drawingMainId, advancedOptionsVisible }) => {
  const activeSlots = combos.flatMap((group) => {
    const activeRelation = group.relations.find((relation) => activeCombosByGuestName[group.guest_name] === relation.id);
    if (!activeRelation) { return []; }
    if (!relationHasRenderableOptions(activeRelation)) { return []; }

    return [{ guestName: group.guest_name, relation: activeRelation }];
  });

  const handleRemove = (guestName) => {
    setActiveCombosByGuestName((previous) => {
      const { [guestName]: _removed, ...rest } = previous;
      return rest;
    });
  };

  // Re-hydrate guest ask_values from localStorage once per combo activation, so a
  // refresh (or reactivating the same combo) starts with the last submitted values
  // instead of option.default_value. The emitted event is the same one OptionsCommon
  // already listens for; targeting guest_drawing.id routes it to the guest fields.
  const activeRelationIds = activeSlots.map(({ relation }) => relation.id).join(',');
  useEffect(() => {
    if (activeSlots.length === 0) { return; }
    const stored = readStoredFormulaOptions(drawingMainId, unit);
    if (!stored) { return; }

    activeSlots.forEach(({ relation }) => {
      (relation.ask_options || []).forEach((option) => {
        const storedValue = stored[`ask_values[${relation.id}][${option.name}]`];
        if (storedValue === undefined || storedValue === null || storedValue === '') { return; }
        emitEvent(Constants.drawing_option_set_value, {
          id: relation.guest_drawing.id,
          name: option.name,
          value: storedValue
        });
      });
    });
  }, [activeRelationIds, drawingMainId, unit]);

  if (activeSlots.length === 0) { return null; }

  return (
    <div className={styles.combo_guest_options_section}>
      {activeSlots.map(({ guestName, relation }) => {
        const guestLabel = relation.guest_drawing.catalog_name
          || relation.guest_drawing.friendly_name
          || relation.guest_drawing.name;
        const labelGroup = `combo_guest_${relation.id}`;
        return (
          <div key={relation.id} className={styles.combo_guest_options_group}>
            <strong className={styles.combo_guest_options_title}>
              {guestName}
            </strong>
            <div className={styles.combo_guest_options_subtitle}>{guestLabel}</div>
            <ComboGuestMaterialChooser
              relation={relation}
              unit={unit}
              register={register}
              errors={errors}
              setRedrawRequested={setRedrawRequested}
              drawingMainId={drawingMainId}
            />
            {visibleAskOptions(relation.ask_options || [], advancedOptionsVisible).map((option) => renderOption({
              id: relation.guest_drawing.id,
              drawing: relation.guest_drawing,
              option,
              unit,
              optionsUserChanged: EMPTY_LIST,
              addToOptionsUserChanged: NO_OP,
              setRedrawRequested,
              labelGroup,
              inputName: `ask_values[${relation.id}][${option.name}]`
            }))}
            {relation.enabled === false && (
              <div className={styles.combo_admin_warning}>{ADMIN_DISABLED_WARNING}</div>
            )}
            <div className={styles.combo_guest_remove_link}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleRemove(guestName); }}
              >
                <i className="fa fa-trash-o" aria-hidden="true"></i> {t(`drawing.combo_remove_${guestName}`)}
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComboGuestOptionsSection;
